/**
 * ChangeReviewPanel
 * Displays enrichment-suggested changes for user review.
 * Users can approve/reject individual changes or use bulk actions.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Loader2,
  CheckSquare,
  ChevronDown,
  ChevronUp,
  ArrowRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  memberEnrichmentAPI,
  PendingChange,
} from '@/services/api/memberEnrichment';

interface ChangeReviewPanelProps {
  memberId: number;
  onChangesApplied?: () => void;
}

const confidenceColor = (c: number) => {
  if (c >= 0.8) return 'text-green-600 dark:text-green-400';
  if (c >= 0.5) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
};

const statusBadge = (status: string) => {
  switch (status) {
    case 'pending':
      return <Badge variant="outline" className="text-xs gap-1"><Clock className="h-3 w-3" />Pending</Badge>;
    case 'applied':
      return <Badge className="text-xs bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 gap-1"><CheckCircle className="h-3 w-3" />Applied</Badge>;
    case 'rejected':
      return <Badge className="text-xs bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 gap-1"><XCircle className="h-3 w-3" />Rejected</Badge>;
    case 'expired':
      return <Badge variant="secondary" className="text-xs gap-1"><Clock className="h-3 w-3" />Expired</Badge>;
    default:
      return <Badge variant="outline" className="text-xs">{status}</Badge>;
  }
};

export default function ChangeReviewPanel({ memberId, onChangesApplied }: ChangeReviewPanelProps) {
  const [changes, setChanges] = useState<PendingChange[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const loadChanges = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Load pending changes first
      const pendingRes = await memberEnrichmentAPI.getPendingChanges(memberId, 'pending');
      const pending = pendingRes.changes || [];

      // Also load recently reviewed for history
      let history: PendingChange[] = [];
      try {
        const appliedRes = await memberEnrichmentAPI.getPendingChanges(memberId, 'applied');
        const rejectedRes = await memberEnrichmentAPI.getPendingChanges(memberId, 'rejected');
        history = [...(appliedRes.changes || []), ...(rejectedRes.changes || [])];
        history.sort((a, b) => new Date(b.reviewed_at || b.created_at).getTime() - new Date(a.reviewed_at || a.created_at).getTime());
      } catch {
        // History is optional
      }

      setChanges([...pending, ...history]);
    } catch {
      setError('Failed to load pending changes');
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => { loadChanges(); }, [loadChanges]);

  const pendingChanges = changes.filter(c => c.status === 'pending');
  const reviewedChanges = changes.filter(c => c.status !== 'pending');

  const handleApprove = async (changeId: number) => {
    setProcessing(prev => new Set(prev).add(changeId));
    try {
      await memberEnrichmentAPI.approveChange(changeId);
      await loadChanges();
      onChangesApplied?.();
    } catch {
      setError('Failed to approve change');
    } finally {
      setProcessing(prev => { const s = new Set(prev); s.delete(changeId); return s; });
    }
  };

  const handleReject = async (changeId: number) => {
    setProcessing(prev => new Set(prev).add(changeId));
    try {
      await memberEnrichmentAPI.rejectChange(changeId);
      await loadChanges();
    } catch {
      setError('Failed to reject change');
    } finally {
      setProcessing(prev => { const s = new Set(prev); s.delete(changeId); return s; });
    }
  };

  const handleBulkApprove = async () => {
    const ids = selected.size > 0 ? Array.from(selected) : pendingChanges.map(c => c.id);
    if (ids.length === 0) return;
    setProcessing(new Set(ids));
    try {
      await memberEnrichmentAPI.bulkApproveChanges(ids);
      setSelected(new Set());
      await loadChanges();
      onChangesApplied?.();
    } catch {
      setError('Failed to bulk approve changes');
    } finally {
      setProcessing(new Set());
    }
  };

  const handleBulkReject = async () => {
    const ids = selected.size > 0 ? Array.from(selected) : pendingChanges.map(c => c.id);
    if (ids.length === 0) return;
    setProcessing(new Set(ids));
    try {
      await memberEnrichmentAPI.bulkRejectChanges(ids);
      setSelected(new Set());
      await loadChanges();
    } catch {
      setError('Failed to bulk reject changes');
    } finally {
      setProcessing(new Set());
    }
  };

  const toggleSelect = (id: number) => {
    setSelected(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === pendingChanges.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(pendingChanges.map(c => c.id)));
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-blue-500 mr-2" />
          <span className="text-sm text-gray-500">Loading pending changes...</span>
        </CardContent>
      </Card>
    );
  }

  if (pendingChanges.length === 0 && reviewedChanges.length === 0) {
    return null; // Don't render if no changes at all
  }

  return (
    <Card className="border-l-4 border-amber-300 dark:border-amber-700">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center justify-between">
          <span className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            Enrichment Changes for Review
            {pendingChanges.length > 0 && (
              <Badge className="text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                {pendingChanges.length} pending
              </Badge>
            )}
          </span>
          {pendingChanges.length > 1 && (
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={toggleSelectAll}>
                <CheckSquare className="h-3 w-3" />
                {selected.size === pendingChanges.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Button
                size="sm"
                className="h-7 text-xs gap-1 bg-v2accent hover:bg-v2accent/90"
                onClick={handleBulkApprove}
                disabled={processing.size > 0}
              >
                {processing.size > 0 ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />}
                Approve {selected.size > 0 ? `(${selected.size})` : 'All'}
              </Button>
              <Button
                size="sm"
                variant="destructive"
                className="h-7 text-xs gap-1"
                onClick={handleBulkReject}
                disabled={processing.size > 0}
              >
                <XCircle className="h-3 w-3" />
                Reject {selected.size > 0 ? `(${selected.size})` : 'All'}
              </Button>
            </div>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {error && (
          <Alert variant="destructive" className="mb-3">
            <AlertDescription className="text-xs">{error}</AlertDescription>
          </Alert>
        )}

        {/* Pending changes */}
        {pendingChanges.length > 0 && (
          <div className="space-y-2">
            {pendingChanges.map((change) => {
              const isProcessing = processing.has(change.id);
              const isSelected = selected.has(change.id);
              return (
                <div
                  key={change.id}
                  className={`border rounded-lg p-3 transition-colors ${
                    isSelected ? 'border-blue-300 bg-blue-50/50 dark:border-blue-700 dark:bg-blue-900/20' : 'dark:border-gray-700'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2 min-w-0 flex-1">
                      {pendingChanges.length > 1 && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(change.id)}
                          className="mt-1 rounded border-gray-300"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {change.field_label || change.field_name.replace(/_/g, ' ')}
                          </span>
                          {change.is_custom_field && (
                            <Badge variant="outline" className="text-[10px]">Custom Field</Badge>
                          )}
                          <Badge variant="outline" className="text-[10px]">
                            {change.module_label || change.module_key.replace(/_/g, ' ')}
                          </Badge>
                          {change.confidence > 0 && (
                            <span className={`text-xs font-medium ${confidenceColor(change.confidence)}`}>
                              {(change.confidence * 100).toFixed(0)}% confidence
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1.5 text-xs">
                          <span className="text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded font-mono truncate max-w-[200px]">
                            {change.old_value || '(empty)'}
                          </span>
                          <ArrowRight className="h-3 w-3 text-gray-400 flex-shrink-0" />
                          <span className="text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded font-mono truncate max-w-[200px]">
                            {change.new_value}
                          </span>
                        </div>
                        {change.reason && (
                          <p className="text-xs text-gray-500 mt-1">{change.reason}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1.5 flex-shrink-0">
                      <Button
                        size="sm"
                        className="h-7 text-xs gap-1 bg-v2accent hover:bg-v2accent/90"
                        onClick={() => handleApprove(change.id)}
                        disabled={isProcessing}
                      >
                        {isProcessing ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />}
                        Apply
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="h-7 text-xs gap-1"
                        onClick={() => handleReject(change.id)}
                        disabled={isProcessing}
                      >
                        <XCircle className="h-3 w-3" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {pendingChanges.length === 0 && reviewedChanges.length > 0 && (
          <p className="text-sm text-gray-500 py-2">No pending changes. All enrichment suggestions have been reviewed.</p>
        )}

        {/* History of reviewed changes */}
        {reviewedChanges.length > 0 && (
          <div className="mt-3">
            <button
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              onClick={() => setShowHistory(!showHistory)}
            >
              {showHistory ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              Review History ({reviewedChanges.length})
            </button>
            {showHistory && (
              <div className="space-y-1.5 mt-2">
                {reviewedChanges.slice(0, 20).map((change) => (
                  <div key={change.id} className="flex items-center justify-between text-xs p-2 rounded bg-gray-50 dark:bg-gray-800/50">
                    <div className="flex items-center gap-2 min-w-0">
                      {statusBadge(change.status)}
                      <span className="font-medium truncate">
                        {change.field_label || change.field_name.replace(/_/g, ' ')}
                      </span>
                      <span className="text-gray-400 truncate">
                        {change.old_value || '(empty)'} → {change.new_value}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 text-gray-400">
                      {change.reviewer_name && <span>by {change.reviewer_name}</span>}
                      {change.reviewed_at && <span>{new Date(change.reviewed_at).toLocaleDateString()}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export { ChangeReviewPanel };
